import { useState } from 'react';
import type { ReactNode } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import type { CharacterDraft } from '../../stores/characterCreationStore';
import type { RpCustomSection } from '../../stores/playSessionStore';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { BondIcon, IdealIcon, FlawIcon } from './PlayModeIcons';
import { mergeWithDefaults } from './rpSectionUtils';

// Format currency with proper coin names
const COIN_NAMES: Record<string, { name: string; color: string }> = {
  gp: { name: 'Gold', color: 'text-accent-gold' },
  sp: { name: 'Silver', color: 'text-text-secondary' },
  cp: { name: 'Copper', color: 'text-class-fighter' },
  ep: { name: 'Electrum', color: 'text-class-bard' },
  pp: { name: 'Platinum', color: 'text-class-paladin' },
};

interface RpSectionRendererProps {
  sectionOrder: string[];
  hiddenSections: string[];
  customSections: RpCustomSection[];
  benefits: CharacterBenefits;
  draft: CharacterDraft;
  sessionNotes: string;
  onNotesChange: (notes: string) => void;
  onCustomSectionContentChange: (id: string, content: string) => void;
}

interface SectionRenderContext {
  benefits: CharacterBenefits;
  draft: CharacterDraft;
  sessionNotes: string;
  onNotesChange: (notes: string) => void;
  expandedSections: Set<string>;
  toggleSection: (id: string) => void;
  expandedMotivation: string | null;
  setExpandedMotivation: (id: string | null) => void;
  personalityExpanded: boolean;
  setPersonalityExpanded: (expanded: boolean) => void;
}

type SectionEntry = {
  label: string;
  render: (ctx: SectionRenderContext) => ReactNode | null;
};

function buildSectionRegistry(): Record<string, SectionEntry> {
  return {
    identity: {
      label: 'Identity Card',
      render: (ctx) => <IdentityCard benefits={ctx.benefits} draft={ctx.draft} />,
    },
    personality: {
      label: 'Personality',
      render: (ctx) => {
        if (ctx.draft.personalityTraits.length === 0) return null;
        return (
          <button
            onClick={() => ctx.setPersonalityExpanded(!ctx.personalityExpanded)}
            className="w-full bg-background-card border border-background-elevated rounded-lg p-3 text-left
              hover:border-accent-bronze/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-display text-xs text-accent-gold uppercase tracking-wider">Personality</span>
              <span className="text-text-muted text-xs">{ctx.personalityExpanded ? '\u25BC' : '\u25B6'}</span>
            </div>
            <p className={`text-sm text-text-secondary italic leading-relaxed ${ctx.personalityExpanded ? '' : 'line-clamp-2'}`}>
              &ldquo;{ctx.draft.personalityTraits.join('. ')}&rdquo;
            </p>
          </button>
        );
      },
    },
    motivations: {
      label: 'Motivations',
      render: (ctx) => {
        const hasBond = ctx.draft.bonds.length > 0;
        const hasIdeal = ctx.draft.ideals.length > 0;
        const hasFlaw = ctx.draft.flaws.length > 0;
        if (!hasBond && !hasIdeal && !hasFlaw) return null;
        return (
          <div className="bg-background-card border border-background-elevated rounded-lg divide-y divide-background-elevated overflow-hidden">
            <MotivationRow
              icon={BondIcon}
              label="Bond"
              value={hasBond ? ctx.draft.bonds.join('. ') : undefined}
              isExpanded={ctx.expandedMotivation === 'bond'}
              onToggle={() => ctx.setExpandedMotivation(ctx.expandedMotivation === 'bond' ? null : 'bond')}
              iconColor="text-class-wizard"
            />
            <MotivationRow
              icon={IdealIcon}
              label="Ideal"
              value={hasIdeal ? ctx.draft.ideals.join('. ') : undefined}
              isExpanded={ctx.expandedMotivation === 'ideal'}
              onToggle={() => ctx.setExpandedMotivation(ctx.expandedMotivation === 'ideal' ? null : 'ideal')}
              iconColor="text-accent-gold"
            />
            <MotivationRow
              icon={FlawIcon}
              label="Flaw"
              value={hasFlaw ? ctx.draft.flaws.join('. ') : undefined}
              isExpanded={ctx.expandedMotivation === 'flaw'}
              onToggle={() => ctx.setExpandedMotivation(ctx.expandedMotivation === 'flaw' ? null : 'flaw')}
              iconColor="text-error"
            />
          </div>
        );
      },
    },
    backstory: {
      label: 'Backstory',
      render: (ctx) => {
        if (!ctx.draft.backstory) return null;
        return (
          <CollapsibleSection
            title="Backstory"
            isExpanded={ctx.expandedSections.has('backstory')}
            onToggle={() => ctx.toggleSection('backstory')}
          >
            <div className="text-sm text-text-secondary leading-relaxed space-y-3">
              {ctx.draft.backstory.split('\n\n').map((paragraph: string, i: number) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </CollapsibleSection>
        );
      },
    },
    features: {
      label: 'Heritage & Training',
      render: (ctx) => {
        if (ctx.benefits.racialTraits.length === 0 && ctx.benefits.classFeatures.length === 0) return null;
        return (
          <CollapsibleSection
            title="Heritage & Training"
            badge={ctx.benefits.racialTraits.length + ctx.benefits.classFeatures.length}
            isExpanded={ctx.expandedSections.has('features')}
            onToggle={() => ctx.toggleSection('features')}
          >
            <div className="space-y-2">
              {ctx.benefits.racialTraits.map((trait) => (
                <FeatureItem key={trait.id} name={trait.name} source="Race" description={trait.description} />
              ))}
              {ctx.benefits.classFeatures.map((feature) => (
                <FeatureItem key={feature.id} name={feature.name} source={`Lvl ${feature.level}`} description={feature.description} />
              ))}
            </div>
          </CollapsibleSection>
        );
      },
    },
    tools: {
      label: 'Tool Proficiencies',
      render: (ctx) => {
        if (ctx.benefits.tools.length === 0) return null;
        return (
          <CollapsibleSection
            title="Tool Proficiencies"
            badge={ctx.benefits.tools.length}
            isExpanded={ctx.expandedSections.has('tools')}
            onToggle={() => ctx.toggleSection('tools')}
          >
            <div className="flex flex-wrap gap-2">
              {ctx.benefits.tools.map((tool) => (
                <span key={tool} className="px-2 py-1 bg-background-elevated rounded text-sm text-text-secondary">
                  {tool}
                </span>
              ))}
            </div>
          </CollapsibleSection>
        );
      },
    },
    feats: {
      label: 'Feats',
      render: (ctx) => {
        if (ctx.benefits.feats.length === 0) return null;
        return (
          <CollapsibleSection
            title="Feats"
            badge={ctx.benefits.feats.length}
            isExpanded={ctx.expandedSections.has('feats')}
            onToggle={() => ctx.toggleSection('feats')}
          >
            <div className="space-y-3">
              {ctx.benefits.feats.map((feat) => (
                <div key={feat.id}>
                  <div className="font-display text-sm text-text-primary mb-1">{feat.name}</div>
                  <p className="text-xs text-text-secondary leading-relaxed">{feat.description}</p>
                  {feat.benefits.length > 0 && (
                    <ul className="mt-1 text-xs text-text-muted list-disc list-inside">
                      {feat.benefits.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        );
      },
    },
    equipment: {
      label: 'Equipment & Wealth',
      render: (ctx) => {
        const totalGold = ctx.draft.currency
          ? (ctx.draft.currency.pp || 0) * 10 +
            (ctx.draft.currency.gp || 0) +
            (ctx.draft.currency.ep || 0) * 0.5 +
            (ctx.draft.currency.sp || 0) * 0.1 +
            (ctx.draft.currency.cp || 0) * 0.01
          : 0;
        if (!(ctx.draft.customEquipment?.length > 0 || totalGold > 0)) return null;
        return (
          <CollapsibleSection
            title="Equipment & Wealth"
            badge={totalGold > 0 ? `${Math.floor(totalGold)} gp` : undefined}
            isExpanded={ctx.expandedSections.has('equipment')}
            onToggle={() => ctx.toggleSection('equipment')}
          >
            {ctx.draft.currency && Object.entries(ctx.draft.currency).some(([, val]) => val > 0) && (
              <div className="mb-3">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(ctx.draft.currency).map(([coin, amount]) => {
                    if (amount <= 0) return null;
                    const coinInfo = COIN_NAMES[coin];
                    return (
                      <span key={coin} className="px-2 py-1 bg-background-elevated rounded text-xs">
                        <span className={`font-mono font-bold ${coinInfo?.color || 'text-text-primary'}`}>{amount}</span>
                        <span className="text-text-muted ml-1">{coin}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {ctx.draft.customEquipment && ctx.draft.customEquipment.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {ctx.draft.customEquipment.map((item, i) => (
                  <span key={i} className="px-2 py-1 bg-background-elevated rounded text-xs text-text-secondary">
                    {item}
                  </span>
                ))}
              </div>
            )}
          </CollapsibleSection>
        );
      },
    },
    notes: {
      label: 'Session Notes',
      render: (ctx) => (
        <CollapsibleSection
          title="Session Notes"
          badge={ctx.sessionNotes.trim() ? `${ctx.sessionNotes.trim().split(/\s+/).length} words` : undefined}
          isExpanded={ctx.expandedSections.has('notes')}
          onToggle={() => ctx.toggleSection('notes')}
        >
          <textarea
            value={ctx.sessionNotes}
            onChange={(e) => ctx.onNotesChange(e.target.value)}
            placeholder="Write your session notes here..."
            className="w-full min-h-[120px] p-3 bg-background-elevated border border-background-elevated rounded
              text-sm text-text-secondary leading-relaxed resize-y
              focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
          />
        </CollapsibleSection>
      ),
    },
  };
}

export function RpSectionRenderer({
  sectionOrder,
  hiddenSections,
  customSections,
  benefits,
  draft,
  sessionNotes,
  onNotesChange,
  onCustomSectionContentChange,
}: RpSectionRendererProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedMotivation, setExpandedMotivation] = useState<string | null>(null);
  const [personalityExpanded, setPersonalityExpanded] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const registry = buildSectionRegistry();
  const mergedOrder = mergeWithDefaults(sectionOrder);
  const hiddenSet = new Set(hiddenSections);

  const ctx: SectionRenderContext = {
    benefits,
    draft,
    sessionNotes,
    onNotesChange,
    expandedSections,
    toggleSection,
    expandedMotivation,
    setExpandedMotivation,
    personalityExpanded,
    setPersonalityExpanded,
  };

  const renderedSections: ReactNode[] = [];

  for (const sectionId of mergedOrder) {
    // User-hidden filter
    if (hiddenSet.has(sectionId)) continue;

    // Built-in section
    const entry = registry[sectionId];
    if (entry) {
      const content = entry.render(ctx);
      // Data-empty filter
      if (content === null) continue;
      renderedSections.push(<div key={sectionId}>{content}</div>);
      continue;
    }

    // Custom section
    const customSection = customSections.find((s) => s.id === sectionId);
    if (customSection) {
      renderedSections.push(
        <CustomSectionView
          key={customSection.id}
          section={customSection}
          isExpanded={expandedSections.has(customSection.id)}
          onToggle={() => toggleSection(customSection.id)}
          onContentChange={(content) => onCustomSectionContentChange(customSection.id, content)}
        />
      );
    }
  }

  if (renderedSections.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted text-sm">
        All sections are hidden. Tap the gear icon to customize your layout.
      </div>
    );
  }

  return <div className="max-w-2xl mx-auto space-y-4">{renderedSections}</div>;
}

// --- Helper Components ---

function IdentityCard({ benefits, draft }: { benefits: CharacterBenefits; draft: CharacterDraft }) {
  const characterName = draft.name || 'Unnamed Character';
  const classString = benefits.classLevels.map((c) => `${c.className} ${c.level}`).join(' / ');

  const appearanceChips: { label: string; value: string }[] = [];
  if (draft.appearance?.eyes) appearanceChips.push({ label: 'Eyes', value: draft.appearance.eyes });
  if (draft.appearance?.hair) appearanceChips.push({ label: 'Hair', value: draft.appearance.hair });
  if (draft.appearance?.height) appearanceChips.push({ label: 'Height', value: draft.appearance.height });
  if (draft.appearance?.skin) appearanceChips.push({ label: 'Skin', value: draft.appearance.skin });
  if (draft.appearance?.age) appearanceChips.push({ label: 'Age', value: draft.appearance.age });

  return (
    <div className="bg-background-card border border-background-elevated rounded-lg p-4">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 flex-shrink-0 rounded-full border-2 border-accent-gold bg-background-elevated
          flex items-center justify-center text-2xl font-display text-accent-gold">
          {characterName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl text-text-primary tracking-wide truncate">
            {characterName}
          </h1>
          <p className="text-sm text-text-secondary">
            Level {benefits.characterLevel} {classString}
          </p>
          {draft.alignment && (
            <p className="text-xs text-text-muted mt-0.5">{draft.alignment}</p>
          )}
        </div>
      </div>
      {appearanceChips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {appearanceChips.map(({ label, value }) => (
            <span key={label} className="px-2 py-0.5 bg-background-elevated rounded text-xs text-text-secondary">
              <span className="text-text-muted">{label}:</span> {value}
            </span>
          ))}
        </div>
      )}
      {benefits.languages.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-text-muted">Languages:</span>
          <span className="text-text-secondary">{benefits.languages.join(', ')}</span>
        </div>
      )}
      {benefits.spellcasting && (
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <span className="px-2 py-1 bg-class-warlock/10 border border-class-warlock/20 rounded">
            <span className="text-text-muted">Save DC </span>
            <span className="font-mono font-bold text-class-warlock">{benefits.spellcasting.saveDC}</span>
          </span>
          <span className="px-2 py-1 bg-class-wizard/10 border border-class-wizard/20 rounded">
            <span className="text-text-muted">Attack </span>
            <span className="font-mono font-bold text-class-wizard">
              {benefits.spellcasting.attackBonus >= 0 ? '+' : ''}{benefits.spellcasting.attackBonus}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

function CustomSectionView({
  section,
  isExpanded,
  onToggle,
  onContentChange,
}: {
  section: RpCustomSection;
  isExpanded: boolean;
  onToggle: () => void;
  onContentChange: (content: string) => void;
}) {
  return (
    <CollapsibleSection
      title={section.title}
      badge={section.content.trim() ? `${section.content.trim().split(/\s+/).length} words` : undefined}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      <textarea
        value={section.content}
        onChange={(e) => onContentChange(e.target.value)}
        placeholder={`Write your ${section.title.toLowerCase()} here...`}
        className="w-full min-h-[120px] p-3 bg-background-elevated border border-background-elevated rounded
          text-sm text-text-secondary leading-relaxed resize-y
          focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
      />
    </CollapsibleSection>
  );
}

function MotivationRow({
  icon: Icon,
  label,
  value,
  isExpanded,
  onToggle,
  iconColor,
}: {
  icon: React.FC<{ className?: string }>;
  label: string;
  value?: string;
  isExpanded: boolean;
  onToggle: () => void;
  iconColor: string;
}) {
  const truncatedValue = value && value.length > 40 ? value.slice(0, 40) + '\u2026' : value;

  return (
    <button onClick={onToggle} className="w-full p-3 text-left hover:bg-background-elevated/50 transition-colors">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <span className="text-xs text-text-muted uppercase tracking-wider">{label}</span>
          {value ? (
            <p className={`text-sm text-text-secondary mt-0.5 ${isExpanded ? '' : 'truncate'}`}>
              {isExpanded ? value : truncatedValue}
            </p>
          ) : (
            <p className="text-sm text-text-muted italic mt-0.5">Not defined</p>
          )}
        </div>
        {value && value.length > 40 && (
          <span className="text-text-muted text-xs flex-shrink-0">{isExpanded ? '\u25BC' : '\u25B6'}</span>
        )}
      </div>
    </button>
  );
}

function FeatureItem({
  name,
  source,
  description,
}: {
  name: string;
  source: string;
  description: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <button
      onClick={() => setIsExpanded(!isExpanded)}
      className="w-full text-left p-2 rounded hover:bg-background-elevated/50 transition-colors"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-text-primary">{name}</span>
        <span className="text-[10px] px-2 py-0.5 bg-background-elevated rounded text-text-muted">
          {source}
        </span>
      </div>
      {isExpanded && (
        <p className="text-xs text-text-secondary leading-relaxed mt-2">{description}</p>
      )}
    </button>
  );
}

